import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { ClientApi, FileParameter, FileResponse, ImageThumbnailResource, ImageWithoutImageDataResource, SwaggerResponse } from '../client-api.service';
import { Result } from './result';

export interface Image {
    id?: number;
    name?: string;
    description?: string;
    fileName?: string;
    url?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ImagesService {
    private clientApi = inject(ClientApi);
    private imagesRefreshTrigger = signal<boolean>(false);    
    
    readonly images$ = toObservable(this.imagesRefreshTrigger).pipe(
        debounceTime(500),
        switchMap(() => this.clientApi.getImages().pipe(
            map(res => res.result ?? [] as Image[]),
            shareReplay(1),
            catchError(err => throwError(() => new Error(err)))
        ))
    );

    private imagesResult$ = toObservable(this.imagesRefreshTrigger).pipe(
        debounceTime(500),
        switchMap(() => this.clientApi.getImages().pipe(
            map(res => ({ data: res.result } as Result<ImageWithoutImageDataResource[]>)),
            catchError(err => of(({ data: [], error: err } as Result<ImageWithoutImageDataResource[]>))))
        )
    );
    
    private imagesResult = toSignal(this.imagesResult$, { initialValue: ({ data: [] } as Result<ImageWithoutImageDataResource[]>)});
    images = computed(() => this.imagesResult().data || []);
    imagesError = computed(() => this.imagesResult().error || null);

    private thumbnailsRefreshTrigger = signal<boolean>(false);    
    allThumbnails$ = toObservable(this.imagesRefreshTrigger).pipe(
        debounceTime(500),
        switchMap(() => this.clientApi.getThumbnails().pipe(
            map(res => res.result ?? [] as ImageThumbnailResource[]),        
            shareReplay(1),
            catchError(err => throwError(() => new Error(err)))
        ))
    );

    allThumnails = toSignal(this.allThumbnails$, { initialValue: [] });

    deleteImage(id: number): Observable<SwaggerResponse<boolean>> {
        return this.clientApi.deleteImage(id);
    }

    getImageThumbnail(imageId: number): Observable<SwaggerResponse<FileResponse>> {
        return this.clientApi.getImageThumbnail(imageId);
    }

    postImage(fileName: string, file: FileParameter, createdBy: string, orgCode: string): Observable<SwaggerResponse<number>> {
        return this.clientApi.postImage(fileName, file, createdBy, orgCode).pipe(
            tap(() => {
                this.triggerImagesRefresh();
            })
        );
    }

    triggerImagesRefresh() {
        this.imagesRefreshTrigger.set(!this.imagesRefreshTrigger());
    }    

    triggerThumbnailsRefresh() {
        this.thumbnailsRefreshTrigger.set(!this.thumbnailsRefreshTrigger());
    }    
}

export interface FileResponseWithId {
  fileResponse: FileResponse,
  id: number
}